/**
 * Detectors for template scaffolding rows that vendors ship inside their
 * product sheets and that should never become real product rows.
 *
 * Operate on a row reduced to its string cells. Callers convert from their
 * native shape (ExcelJS values for `readExcelFile`, tab-separated lines for
 * the AI server) before invoking.
 *
 * Server-side `documentToParts.ts` carries its own copy of these rules — the
 * tiny duplication is intentional; the pattern set is small and stable.
 */

const ANNOTATION_PATTERNS: ReadonlyArray<RegExp> = [
  /^l[aä]s mer\b/i, // "Läs mer", "Las mer", "Läs mer/ read more"
  /^read more\b/i,
];

const EXAMPLE_PATTERNS: ReadonlyArray<RegExp> = [/\(exempel\)/i, /\(example\)/i];

const isAnnotationCell = (cell: string): boolean => {
  const trimmed = cell.trim();
  if (!trimmed) return false;
  return ANNOTATION_PATTERNS.some(re => re.test(trimmed));
};

/**
 * True when every non-empty cell in the row is a "Läs mer" / "Read more"
 * annotation marker. Apotea v3.1.4's row 15 fits this — `AK15`/`AM15`/`AN15`
 * carry the annotation, every other cell is null.
 */
export const isAnnotationOnlyRow = (cells: ReadonlyArray<string>): boolean => {
  let hasAny = false;
  for (const cell of cells) {
    if (!cell || !cell.trim()) continue;
    hasAny = true;
    if (!isAnnotationCell(cell)) return false;
  }
  return hasAny;
};

/**
 * True when the row's first non-empty cell carries a parenthesised example
 * marker. Apotea v3.1.4's rows 16–18 fit this — column A holds
 * `Nyhet (exempel)`. Scoped to the leading cell only so a real product
 * description containing `(example)` somewhere in the middle of free-text
 * doesn't get silently dropped.
 *
 * Intentionally does NOT key on placeholder EAN values (e.g. Apotea's
 * `123456789`): a real producer typo would look identical, and validation
 * should surface that as a red EAN error rather than silently drop the row.
 */
export const isExampleRow = (cells: ReadonlyArray<string>): boolean => {
  for (const cell of cells) {
    if (!cell || !cell.trim()) continue;
    return EXAMPLE_PATTERNS.some(re => re.test(cell));
  }
  return false;
};

export const isPhantomRow = (cells: ReadonlyArray<string>): boolean =>
  isAnnotationOnlyRow(cells) || isExampleRow(cells);

/**
 * Minimum non-empty cells for a row to count as real product data.
 *
 * A genuine product fills a name plus at least one identifier (EAN / supplier
 * / price), so a row with fewer than this many populated cells is template
 * scaffolding — e.g. Apotea drags an `=IF(...)` status formula down a single
 * column to ~row 500, producing rows whose only content is a lone "0".
 */
export const MIN_NON_EMPTY_CELLS_FOR_DATA_ROW = 2;

/**
 * True when the row has fewer than `MIN_NON_EMPTY_CELLS_FOR_DATA_ROW`
 * non-empty cells. Distinct from `isPhantomRow` (annotation / example markers)
 * and, like it, only applied on the opt-in vendor-template cleanup path (see
 * `dropPhantomRows` in `readExcelFile`) — NEVER to standard Foodla uploads,
 * where a producer's legitimately sparse row should stay visible for them to
 * fix in the review grid.
 */
export const isSparseRow = (cells: ReadonlyArray<string>): boolean => {
  let count = 0;
  for (const cell of cells) {
    if (cell && cell.trim() !== '') {
      count += 1;
      if (count >= MIN_NON_EMPTY_CELLS_FOR_DATA_ROW) return false;
    }
  }
  return true;
};
